import { useEffect, useMemo, useState } from 'react';
import { usePayableTaskGroupPhasesList } from '../../api/payableTaskGroupPhases';
import { todayIsoDate } from '../../api/suppliers';
import { DECIMAL_INPUT_STEP, formatDecimalInputValue } from '../../lib/decimalInput';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { Select } from '../ui/Select';

export type ManualLineCostKind = 'material' | 'labor';

export type ManualLineFormBody = {
  qty: number | null;
  item_id: string;
  ref_num: string | null;
  payable_task_group: string | null;
  unit_cost: number | null;
  effective_date: string;
  cost_kind?: ManualLineCostKind;
};

export type AddManualLineSubmitBody = ManualLineFormBody & {
  mto_file_id: number;
};

type ManualLineFields = {
  item: string;
  qty: string;
  unit: string;
  refNum: string;
  taskGroup: string;
  effectiveDate: string;
  costKind: ManualLineCostKind | '';
};

function emptyFields(): ManualLineFields {
  return {
    item: '',
    qty: '1',
    unit: '',
    refNum: '',
    taskGroup: '',
    effectiveDate: todayIsoDate(),
    costKind: '',
  };
}

function fieldsFromInitial(initialValues?: {
  item_id: string;
  qty: number | null;
  unit_cost: number | null;
  ref_num: string;
  payable_task_group: string;
}): ManualLineFields {
  if (!initialValues) return emptyFields();
  return {
    item: initialValues.item_id,
    qty: initialValues.qty != null ? String(initialValues.qty) : '',
    unit: initialValues.unit_cost != null ? String(initialValues.unit_cost) : '',
    refNum: initialValues.ref_num,
    taskGroup: initialValues.payable_task_group,
    effectiveDate: todayIsoDate(),
    costKind: '',
  };
}

function parseFields(
  fields: ManualLineFields,
  { requireCostKind }: { requireCostKind: boolean }
): ManualLineFormBody | null {
  if (!fields.item.trim()) return null;
  if (requireCostKind) {
    if (fields.costKind !== 'material' && fields.costKind !== 'labor') return null;
    if (fields.costKind === 'labor' && !fields.taskGroup.trim()) return null;
  }
  const body: ManualLineFormBody = {
    item_id: fields.item.trim(),
    qty: fields.qty === '' ? null : Number(fields.qty),
    unit_cost: fields.unit === '' ? null : Number(fields.unit),
    ref_num: fields.refNum.trim() || null,
    payable_task_group: fields.taskGroup.trim() || null,
    effective_date: fields.effectiveDate,
  };
  if (requireCostKind && (fields.costKind === 'material' || fields.costKind === 'labor')) {
    body.cost_kind = fields.costKind;
  }
  return body;
}

function canSubmitFields(
  fields: ManualLineFields,
  { requireCostKind }: { requireCostKind: boolean }
): boolean {
  if (!fields.item.trim()) return false;
  if (!requireCostKind) return true;
  if (fields.costKind !== 'material' && fields.costKind !== 'labor') return false;
  if (fields.costKind === 'labor' && !fields.taskGroup.trim()) return false;
  return true;
}

function ManualLineFormFields({
  fields,
  setFields,
  taskGroupOptions,
  requireCostKind,
}: {
  fields: ManualLineFields;
  setFields: (next: ManualLineFields) => void;
  taskGroupOptions: string[];
  requireCostKind: boolean;
}) {
  const laborSelected = fields.costKind === 'labor';
  return (
    <>
      <Input
        label="Item Name"
        value={fields.item}
        onChange={(e) => setFields({ ...fields, item: e.target.value })}
        required
      />
      {requireCostKind ? (
        <Select
          label="Cost Kind"
          value={fields.costKind}
          onChange={(e) =>
            setFields({
              ...fields,
              costKind: e.target.value as ManualLineCostKind | '',
            })
          }
          required
        >
          <option value="">Select…</option>
          <option value="material">Materials</option>
          <option value="labor">Labor</option>
        </Select>
      ) : null}
      <Input
        label="Qty"
        type="number"
        step={DECIMAL_INPUT_STEP}
        inputMode="decimal"
        value={fields.qty}
        style={{ textAlign: 'right' }}
        onChange={(e) => setFields({ ...fields, qty: e.target.value })}
        onBlur={(e) => {
          const formatted = formatDecimalInputValue(e.target.value);
          if (formatted !== e.target.value) setFields({ ...fields, qty: formatted });
        }}
      />
      <Input
        label="Unit Cost"
        type="number"
        step={DECIMAL_INPUT_STEP}
        inputMode="decimal"
        value={fields.unit}
        style={{ textAlign: 'right' }}
        onChange={(e) => setFields({ ...fields, unit: e.target.value })}
        onBlur={(e) => {
          const formatted = formatDecimalInputValue(e.target.value);
          if (formatted !== e.target.value) setFields({ ...fields, unit: formatted });
        }}
      />
      <Input
        label="Ref #"
        value={fields.refNum}
        onChange={(e) => setFields({ ...fields, refNum: e.target.value })}
      />
      <Select
        label="Payable Task Group"
        value={fields.taskGroup}
        onChange={(e) => setFields({ ...fields, taskGroup: e.target.value })}
        required={laborSelected}
      >
        <option value="">{laborSelected ? 'Select…' : 'None'}</option>
        {taskGroupOptions.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </Select>
      <Input
        label="Effective Date"
        type="date"
        value={fields.effectiveDate}
        onChange={(e) => setFields({ ...fields, effectiveDate: e.target.value })}
        required
      />
    </>
  );
}

function useTaskGroupOptions(taskGroup: string) {
  const { data: taskGroupRows } = usePayableTaskGroupPhasesList();
  return useMemo(() => {
    const names = (taskGroupRows ?? [])
      .map((r) => r.payable_task_group.trim())
      .filter(Boolean);
    if (taskGroup.trim()) names.push(taskGroup.trim());
    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }, [taskGroupRows, taskGroup]);
}

export function AddManualLineModal({
  open,
  onClose,
  mtoId,
  onSubmit,
  title = 'Add Manual Line',
  submitLabel = 'Add Line',
  initialValues,
  requireCostKind = !initialValues,
}: {
  open: boolean;
  onClose: () => void;
  mtoId: string | undefined;
  title?: string;
  submitLabel?: string;
  initialValues?: {
    item_id: string;
    qty: number | null;
    unit_cost: number | null;
    ref_num: string;
    payable_task_group: string;
  };
  /** When true (default for create), ask Materials/Labor and require task group for Labor. */
  requireCostKind?: boolean;
  onSubmit: (body: AddManualLineSubmitBody) => void | Promise<void>;
}) {
  const [fields, setFields] = useState<ManualLineFields>(emptyFields);
  const taskGroupOptions = useTaskGroupOptions(fields.taskGroup);

  useEffect(() => {
    if (!open) return;
    setFields(fieldsFromInitial(initialValues));
    // Seed once when the modal opens; parent remounts edit via key.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional open-only sync
  }, [open]);

  return (
    <Modal title={title} open={open} onClose={onClose} showClose={false}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!mtoId) return;
          const parsed = parseFields(fields, { requireCostKind });
          if (!parsed) return;
          await onSubmit({
            mto_file_id: Number(mtoId),
            ...parsed,
          });
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <ManualLineFormFields
          fields={fields}
          setFields={setFields}
          taskGroupOptions={taskGroupOptions}
          requireCostKind={requireCostKind}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="success"
            disabled={!canSubmitFields(fields, { requireCostKind })}
          >
            {submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function AddManualLinesBulkModal({
  open,
  onClose,
  mtos,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  mtos: Array<{ id: number | string; name?: string | null }>;
  isPending?: boolean;
  onSubmit: (mtoFileIds: number[], body: ManualLineFormBody) => void | Promise<void>;
}) {
  const [fields, setFields] = useState<ManualLineFields>(emptyFields);
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [selectMtosOpen, setSelectMtosOpen] = useState(false);
  const [draftSelected, setDraftSelected] = useState<Set<number>>(() => new Set());
  const taskGroupOptions = useTaskGroupOptions(fields.taskGroup);
  const requireCostKind = true;

  const mtoIds = useMemo(
    () => mtos.map((m) => Number(m.id)).filter((id) => Number.isFinite(id) && id > 0),
    [mtos]
  );
  const selectedCount = mtoIds.filter((id) => selected.has(id)).length;
  const draftAllSelected = mtoIds.length > 0 && mtoIds.every((id) => draftSelected.has(id));
  const draftSomeSelected = mtoIds.some((id) => draftSelected.has(id));

  useEffect(() => {
    if (!open) {
      setSelectMtosOpen(false);
      return;
    }
    setFields(emptyFields());
    setSelected(new Set(mtoIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional open-only sync
  }, [open]);

  function openSelectMtos() {
    setDraftSelected(new Set(selected));
    setSelectMtosOpen(true);
  }

  return (
    <>
      <Modal
        title="Add Manual Lines"
        open={open && !selectMtosOpen}
        onClose={onClose}
        showClose={false}
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const parsed = parseFields(fields, { requireCostKind });
            if (!parsed) return;
            const ids = mtoIds.filter((id) => selected.has(id));
            if (!ids.length) return;
            await onSubmit(ids, parsed);
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>MTOs</div>
            {mtoIds.length === 0 ? (
              <p style={{ margin: 0, color: 'var(--color-muted, #6b7280)' }}>No MTOs available.</p>
            ) : (
              <button
                type="button"
                onClick={openSelectMtos}
                disabled={isPending}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: 'var(--color-link, #2563eb)',
                  textDecoration: 'underline',
                  cursor: isPending ? 'default' : 'pointer',
                  font: 'inherit',
                  textAlign: 'left',
                }}
              >
                {selectedCount} MTO{selectedCount === 1 ? '' : 's'} Selected
              </button>
            )}
          </div>
          <ManualLineFormFields
            fields={fields}
            setFields={setFields}
            taskGroupOptions={taskGroupOptions}
            requireCostKind={requireCostKind}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="success"
              disabled={
                isPending ||
                selectedCount === 0 ||
                !canSubmitFields(fields, { requireCostKind })
              }
            >
              {isPending
                ? 'Adding…'
                : `Add To ${selectedCount} MTO${selectedCount === 1 ? '' : 's'}`}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        title="Select MTOs"
        open={open && selectMtosOpen}
        onClose={() => setSelectMtosOpen(false)}
        showClose={false}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {mtoIds.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--color-muted, #6b7280)' }}>No MTOs available.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflow: 'auto' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={draftAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = draftSomeSelected && !draftAllSelected;
                  }}
                  onChange={() => {
                    setDraftSelected(draftAllSelected ? new Set() : new Set(mtoIds));
                  }}
                />
                Select All
              </label>
              {mtos.map((m) => {
                const id = Number(m.id);
                if (!Number.isFinite(id) || id <= 0) return null;
                return (
                  <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={draftSelected.has(id)}
                      onChange={() => {
                        setDraftSelected((prev) => {
                          const next = new Set(prev);
                          if (next.has(id)) next.delete(id);
                          else next.add(id);
                          return next;
                        });
                      }}
                    />
                    {m.name?.trim() ? m.name : `MTO ${id}`}
                  </label>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button type="button" variant="ghost" onClick={() => setSelectMtosOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                setSelected(new Set(draftSelected));
                setSelectMtosOpen(false);
              }}
            >
              Done
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
